import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useMemo, useState } from "react";
import SideBar from "../sidebar/sidebar";
import DunningBanner from "../dunning-banner/dunning-banner";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { useIsDesktop } from "@common/hooks/use-is-mobile";
import ClearUserMenu from "../clear-user-menu/clear-user-menu";

export interface AccountPageProps {
  page: JSX.Element;
  classes?: any;
  readOnly?: boolean;
  /**
   * Chromeless mode (the `/clear-*` flow): drop the sidebar drawer, the mobile
   * app bar and the dunning banner, leaving only the page itself. Mirrors
   * AppView's `bare` — this view has its own shell rather than using AppView.
   */
  bare?: boolean;
}

export interface AccountPageState {
  open: boolean;
}

export const AccountPage = ({ page, classes, readOnly, bare = false }: AccountPageProps) => {
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  const direction = useAppSelector((state) => state.UserReducer.direction);

  const variant = useMemo(() => (isDesktop ? "permanent" : "temporary"), [isDesktop]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const closeDrawer = () => {
    setOpen(false);
  };

  return (
    // One screen tall in total: the mobile app bar takes its height from the
    // row below, instead of pushing a 100vh row past the fold (a page scroll that
    // hid the bottom of full-height pages, such as a docked composer or a Save bar).
    <Box dir={direction ==="ltr" ? "ltr" : "rtl"} sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {!isDesktop && !bare && (
        <AppBar position="relative">
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              className={classes?.menuButton}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap>
              Blueticks
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
        
        {!bare && (
          <Drawer
            variant={variant}
            anchor={direction === "rtl" ? "right" : "left"}
            open={open}
            onClose={handleDrawerToggle}
            sx={{
              width: "300px",
              ".MuiPaper-root": {
                md: {
                },
              },
              [`& .MuiDrawer-paper`]: {
                background: "var(--tasks-menu-bg)",
                borderInlineEnd: "1px solid var(--tasks-menu-border)",
                overflow: 'visible',
                borderRadius: "0 !important",
              },
            }}
          >

            <Box
              onClick={!isDesktop ? closeDrawer : undefined}
              sx={{ display: "flex", flex: 1, minWidth: 0 }}
            >
              <SideBar readOnly={readOnly}></SideBar>
            </Box>
          </Drawer>
        )}
        <Box sx={{ flex: "auto", backgroundColor: "var(--color-bg)", display: "flex", flexDirection: "column", minWidth: 0 }} component="main">
          {!bare && <DunningBanner />}
          {/* The shell is exactly 100vh, so the page must scroll HERE. Without
              overflow the content spilled out, the document scrolled instead, and
              the whole layout slid up leaving an empty band at the bottom. */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {page}
          </Box>
          {/* Same account affordance the clear calendar gets — without it this
              page has no way back and no way out. */}
          {bare && <ClearUserMenu />}
        </Box>
      </Stack>
    </Box>
  );
};
